/**
 * MEMCORE Modul B (Labor) — Dialoge fürs Anwalts-Gedächtnis.
 *
 *   MemorySessionDialog  „Session beenden“: optionale Anweisung → Curator-Vorschau
 *                        (Merkpunkte) → explizit Speichern (Variante A §7.7).
 *   MemoryThumbDialog    Daumen hoch, 2-Schritt: „Wirklich speichern?“ + optionaler
 *                        Kommentar → Speichern (quelle_ref = Portal-Message-ID).
 *   MemoryNudgeDialog    Chat-Beendigung (Schließen/Archivieren/Löschen): mindestens
 *                        1× fragen „Konversation speichern?“ — Nein = nichts speichern.
 *   MemoryOnboardingDialog  Erststart-Interview (~10 Fragen, kein Mandanten-PII).
 *
 * Schreiben ausschließlich über die Curator-Schreibstraße (memcore.ts §7.3).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { BookMarked, Loader2, X, CheckCircle2, AlertTriangle, ThumbsUp, Redo2 } from 'lucide-react';
import { PORTAL_BTN } from '../arkorbit-ui/portalTheme';
import {
  type CuratorResult,
  type MemcoreContext,
  ONBOARDING_FRAGEN,
  curatorFehlertext,
  memcoreDaumenSpeichern,
  memcoreOnboardingSpeichern,
  memcoreSessionSpeichern,
  memcoreVorschau,
} from './memcore';
import {
  AKTE_HANDOFF_CURATOR_ANWEISUNG,
  capAkteHandoff,
  formatSavedChatStatus,
  gateHandoffFeststeht,
  PORTAL_SAVE_FESTSTEHT_HINT,
  PORTAL_SAVE_OFFEN_HINT,
  savedChatStand,
} from './portalArkoThreadBind';

// ── Gemeinsame Modal-Hülle ──────────────────────────────────────────────────

const ModalShell: React.FC<{
  title: string;
  icon?: React.ElementType;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}> = ({ title, icon: Icon = BookMarked, onClose, children, wide = false }) => (
  <div
    className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 pt-[max(1rem,env(safe-area-inset-top))] sm:items-center sm:overflow-hidden sm:pt-4"
    role="dialog"
    aria-modal="true"
    aria-label={title}
  >
    <div
      className={`w-full ${wide ? 'max-w-xl' : 'max-w-md'} rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl`}
    >
      <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
        <Icon className="h-4 w-4 text-blue-400" aria-hidden />
        <h3 className="flex-1 text-sm font-bold text-white">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-slate-500 transition-colors hover:bg-white/[0.06] hover:text-slate-300"
          aria-label="Dialog schließen"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="max-h-[70vh] overflow-y-auto px-4 py-4 custom-scrollbar">{children}</div>
    </div>
  </div>
);

const ResultNote: React.FC<{ result: CuratorResult }> = ({ result }) => {
  if (result.status === 'ok') {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>
          Im Gedächtnis gespeichert
          {result.vermerk_id ? <> — Vermerk <span className="font-mono">{result.vermerk_id}</span></> : null}.
        </span>
      </div>
    );
  }
  if (result.status === 'duplikat') {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>{curatorFehlertext(result)}</span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
      <span>{curatorFehlertext(result)}</span>
    </div>
  );
};

const PII_HINWEIS = 'Bitte keine Mandanten- oder personenbezogenen Daten (Namen, Kontakte, Falldaten).';

// ── Session beenden (Variante A: Vorschau → explizit Speichern) ─────────────

export interface MemorySessionDialogProps {
  ctx: MemcoreContext;
  curatorUrl: string;
  threadId: string;
  threadTitle: string;
  transcript: string;
  onClose: () => void;
  /** Nach erfolgreichem Speichern ODER bewusstem „ohne Speichern beenden“. */
  onFinished: (saved: boolean) => void;
}

export const MemorySessionDialog: React.FC<MemorySessionDialogProps> = ({
  ctx,
  curatorUrl,
  threadId,
  threadTitle,
  transcript,
  onClose,
  onFinished,
}) => {
  const [schritt, setSchritt] = useState<'anweisung' | 'vorschau'>('anweisung');
  const [anweisung, setAnweisung] = useState('');
  const [merkpunkte, setMerkpunkte] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CuratorResult | null>(null);

  const erstelleVorschau = async () => {
    setBusy(true);
    setResult(null);
    const r = await memcoreVorschau(curatorUrl, ctx, transcript, anweisung);
    setBusy(false);
    if (r.status === 'vorschau' && r.merkpunkte) {
      setMerkpunkte(r.merkpunkte);
      setSchritt('vorschau');
    } else {
      setResult(r);
    }
  };

  const speichern = async () => {
    if (!merkpunkte.trim()) return;
    setBusy(true);
    setResult(null);
    const r = await memcoreSessionSpeichern(curatorUrl, ctx, merkpunkte.trim(), threadId, transcript);
    setBusy(false);
    setResult(r);
    if (r.status === 'ok' || r.status === 'duplikat') {
      window.setTimeout(() => onFinished(true), 1200);
    }
  };

  return (
    <ModalShell title={`Session beenden — ${threadTitle}`} onClose={onClose} wide>
      {schritt === 'anweisung' && (
        <div className="space-y-3">
          <p className="text-xs leading-relaxed text-slate-400">
            ARKO fasst diese Konversation zu Merkpunkten zusammen. Sie sehen eine Vorschau und
            entscheiden ausdrücklich, ob gespeichert wird. {PII_HINWEIS}
          </p>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Optionale Anweisung — was soll gemerkt werden?
          </label>
          <textarea
            value={anweisung}
            onChange={(e) => setAnweisung(e.target.value)}
            rows={2}
            placeholder="z. B. „Nur die Formulierungs-Präferenzen merken“ (leer = ARKO entscheidet)"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-blue-500/60 focus:outline-none"
          />
          {result && <ResultNote result={result} />}
          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={() => onFinished(false)}
              className="rounded-lg px-3 py-1.5 text-xs text-slate-400 transition-colors hover:text-slate-200"
            >
              Ohne Speichern beenden
            </button>
            <button
              type="button"
              onClick={erstelleVorschau}
              disabled={busy}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${PORTAL_BTN.primary}`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookMarked className="h-3.5 w-3.5" />}
              {busy ? 'ARKO komprimiert…' : 'Merkpunkte erstellen'}
            </button>
          </div>
        </div>
      )}

      {schritt === 'vorschau' && (
        <div className="space-y-3">
          <p className="text-xs leading-relaxed text-slate-400">
            Vorschau der Merkpunkte — bearbeitbar. Gespeichert wird <strong>genau</strong> dieser Text,
            erst nach Klick auf „Im Gedächtnis speichern“.
          </p>
          <textarea
            value={merkpunkte}
            onChange={(e) => setMerkpunkte(e.target.value)}
            rows={6}
            className="w-full rounded-lg border border-blue-500/40 bg-slate-950 px-3 py-2 text-sm leading-relaxed text-slate-100 focus:border-blue-400 focus:outline-none"
          />
          {result && <ResultNote result={result} />}
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSchritt('anweisung')}
                disabled={busy}
                className="rounded-lg px-3 py-1.5 text-xs text-slate-400 transition-colors hover:text-slate-200"
              >
                Zurück
              </button>
              <button
                type="button"
                onClick={() => onFinished(false)}
                disabled={busy}
                className="rounded-lg px-3 py-1.5 text-xs text-slate-400 transition-colors hover:text-slate-200"
              >
                Ohne Speichern beenden
              </button>
            </div>
            <button
              type="button"
              onClick={speichern}
              disabled={busy || !merkpunkte.trim()}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${PORTAL_BTN.primary}`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Im Gedächtnis speichern
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
};

// ── Weiterarbeiten in derselben Akte (Handoff, Vorschau-first) ───────────────

export const AkteHandoffBanner: React.FC<{
  text: string;
  compact?: boolean;
  hint?: string | null;
}> = ({
  text,
  compact,
  hint,
}) => {
  const body = text.trim();
  if (!body) return null;
  return (
    <aside
      className={`rounded-xl border border-pink-500/25 bg-pink-500/[0.06] ${
        compact ? 'px-2.5 py-1.5 mb-2' : 'px-3 py-2 mb-3'
      }`}
      aria-label="Arbeitsstand dieser Akte"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-pink-200/80 mb-1">
        Arbeitsstand dieser Akte
      </p>
      <pre className="whitespace-pre-wrap font-sans text-[11px] leading-relaxed text-slate-200">
        {body}
      </pre>
      {hint?.trim() ? (
        <p className="mt-1.5 text-[10px] leading-snug text-slate-500">{hint.trim()}</p>
      ) : null}
    </aside>
  );
};

export interface AkteHandoffDialogProps {
  ctx?: MemcoreContext | null;
  curatorUrl?: string;
  threadId: string;
  threadTitle: string;
  transcript: string;
  localDraft: string;
  onClose: () => void;
  onContinue: (handoffText: string) => void;
}

export const AkteHandoffDialog: React.FC<AkteHandoffDialogProps> = ({
  ctx,
  curatorUrl,
  threadId,
  threadTitle,
  transcript,
  localDraft,
  onClose,
  onContinue,
}) => {
  const [handoff, setHandoff] = useState(localDraft);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CuratorResult | null>(null);
  const [saveToMemory, setSaveToMemory] = useState(false);
  const canMemory = Boolean(curatorUrl && ctx);

  const erstelleVorschau = async () => {
    if (!curatorUrl || !ctx) {
      setHandoff(localDraft);
      return;
    }
    setBusy(true);
    setResult(null);
    const r = await memcoreVorschau(curatorUrl, ctx, transcript, AKTE_HANDOFF_CURATOR_ANWEISUNG);
    setBusy(false);
    if (r.status === 'vorschau' && r.merkpunkte) {
      setHandoff(gateHandoffFeststeht(capAkteHandoff(r.merkpunkte)));
    } else {
      setHandoff(localDraft);
      setResult(r);
    }
  };

  useEffect(() => {
    void erstelleVorschau();
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weiterarbeiten = async () => {
    const text = capAkteHandoff(handoff);
    if (!text) return;
    if (saveToMemory && curatorUrl && ctx) {
      setBusy(true);
      setResult(null);
      const r = await memcoreSessionSpeichern(curatorUrl, ctx, text, threadId, transcript);
      setBusy(false);
      setResult(r);
      if (r.status !== 'ok' && r.status !== 'duplikat') return;
    }
    onContinue(text);
  };

  return (
    <ModalShell title={`Weiterarbeiten — ${threadTitle}`} icon={Redo2} onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs leading-relaxed text-slate-400">
          Sichtbarer Arbeitsstand für den nächsten Chat an derselben Akte — kein neuer leerer
          Verlauf und kein Plus-Chat. Bitte prüfen und bei Bedarf korrigieren. Nur was in Chat
          oder Akte steht; nichts erfinden.
        </p>
        <textarea
          value={handoff}
          onChange={(e) => setHandoff(e.target.value)}
          rows={8}
          className="w-full rounded-lg border border-pink-500/40 bg-slate-950 px-3 py-2 text-sm leading-relaxed text-slate-100 focus:border-pink-400 focus:outline-none"
        />
        {canMemory ? (
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={saveToMemory}
              onChange={(e) => setSaveToMemory(e.target.checked)}
              className="rounded border-slate-600"
            />
            Auch ins Gedächtnis (bestehender Vermerk-Pfad)
          </label>
        ) : null}
        {result && result.status !== 'vorschau' ? <ResultNote result={result} /> : null}
        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-3 py-1.5 text-xs text-slate-400 transition-colors hover:text-slate-200"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void weiterarbeiten()}
            disabled={busy || !handoff.trim()}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${PORTAL_BTN.primary}`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Redo2 className="h-3.5 w-3.5" />}
            Neuen Chat in dieser Akte starten
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

// ── Daumen hoch — 2-Schritt ─────────────────────────────────────────────────

export interface MemoryThumbDialogProps {
  ctx: MemcoreContext;
  curatorUrl: string;
  /** quelle_ref = Portal-Message-ID (§7.7) */
  quelleRef: string;
  nachricht: string;
  onClose: () => void;
}

export const MemoryThumbDialog: React.FC<MemoryThumbDialogProps> = ({
  ctx,
  curatorUrl,
  quelleRef,
  nachricht,
  onClose,
}) => {
  const [kommentar, setKommentar] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CuratorResult | null>(null);

  const stand = savedChatStand(nachricht);
  const savedBody = formatSavedChatStatus(nachricht);
  const snippet = useMemo(
    () => (savedBody.length > 360 ? `${savedBody.slice(0, 360)}…` : savedBody),
    [savedBody],
  );

  const speichern = async () => {
    setBusy(true);
    setResult(null);
    const r = await memcoreDaumenSpeichern(curatorUrl, ctx, savedBody, kommentar, quelleRef);
    setBusy(false);
    setResult(r);
    if (r.status === 'ok' || r.status === 'duplikat') {
      window.setTimeout(onClose, 1400);
    }
  };

  return (
    <ModalShell title="Wirklich im Gedächtnis speichern?" icon={ThumbsUp} onClose={onClose}>
      <div className="space-y-3">
        <p data-testid="portal-save-stand" className="text-sm font-semibold text-slate-100">
          {stand}
        </p>
        <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-xs leading-relaxed text-slate-300 whitespace-pre-wrap">
          {snippet}
        </div>
        <p className="text-[11px] text-slate-500">
          {stand === 'Offen' ? PORTAL_SAVE_OFFEN_HINT : PORTAL_SAVE_FESTSTEHT_HINT} {PII_HINWEIS}
        </p>
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Optionaler Kommentar
        </label>
        <textarea
          value={kommentar}
          onChange={(e) => setKommentar(e.target.value)}
          rows={2}
          placeholder="z. B. „So künftig alle Kündigungs-Hinweise formulieren“"
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-blue-500/60 focus:outline-none"
        />
        {result && <ResultNote result={result} />}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-3 py-1.5 text-xs text-slate-400 transition-colors hover:text-slate-200"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={speichern}
            disabled={busy}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${PORTAL_BTN.primary}`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ThumbsUp className="h-3.5 w-3.5" />}
            Speichern
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

// ── Nudge bei Chat-Beendigung ───────────────────────────────────────────────

export interface MemoryNudgeDialogProps {
  threadTitle: string;
  /** „Speichern…“ → Session-Dialog öffnen (Aufrufer führt danach die Aktion aus). */
  onSave: () => void;
  /** „Nein“ → nichts speichern, ursprüngliche Aktion ausführen. */
  onDecline: () => void;
  /** Abbrechen → weder speichern noch Aktion. */
  onCancel: () => void;
}

export const MemoryNudgeDialog: React.FC<MemoryNudgeDialogProps> = ({
  threadTitle,
  onSave,
  onDecline,
  onCancel,
}) => (
  <ModalShell title="Konversation speichern?" onClose={onCancel}>
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-slate-400">
        „{threadTitle}“ wird beendet. Soll ARKO vorher Merkpunkte für Ihr Gedächtnis vorschlagen?
        Bei „Nein“ wird nichts gespeichert — kein stilles Speichern.
      </p>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3 py-1.5 text-xs text-slate-500 transition-colors hover:text-slate-300"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={onDecline}
          className={`rounded-lg border px-3 py-1.5 text-xs ${PORTAL_BTN.secondary}`}
        >
          Nein, nicht speichern
        </button>
        <button
          type="button"
          onClick={onSave}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${PORTAL_BTN.primary}`}
        >
          <BookMarked className="h-3.5 w-3.5" />
          Speichern…
        </button>
      </div>
    </div>
  </ModalShell>
);

// ── Onboarding-Interview (Erststart, §7.7 Cold-Start) ───────────────────────

export interface MemoryOnboardingDialogProps {
  ctx: MemcoreContext;
  curatorUrl: string;
  /** done=true: mindestens ein Vermerk gespeichert; false: übersprungen / nichts gespeichert. */
  onClose: (done: boolean) => void;
  /** Sobald mindestens ein Vermerk angelegt wurde (vor „Fertig“-Klick). */
  onSaved?: () => void;
}

interface OnboardingAntwort {
  frage: string;
  antwort: string;
}

export const MemoryOnboardingDialog: React.FC<MemoryOnboardingDialogProps> = ({
  ctx,
  curatorUrl,
  onClose,
  onSaved,
}) => {
  const [index, setIndex] = useState(0);
  const [eingabe, setEingabe] = useState('');
  const [antworten, setAntworten] = useState<OnboardingAntwort[]>([]);
  const [phase, setPhase] = useState<'fragen' | 'speichern' | 'fertig'>('fragen');
  const [gespeichert, setGespeichert] = useState(0);
  const [fehler, setFehler] = useState<string[]>([]);

  const frage = ONBOARDING_FRAGEN[index];
  const letzte = index === ONBOARDING_FRAGEN.length - 1;

  const weiter = (skip: boolean) => {
    const text = eingabe.trim();
    const naechste = skip || !text ? antworten : [...antworten, { frage, antwort: text }];
    setAntworten(naechste);
    setEingabe('');
    if (!letzte) {
      setIndex(index + 1);
      return;
    }
    if (naechste.length === 0) {
      onClose(false);
      return;
    }
    void speichereAlle(naechste);
  };

  const speichereAlle = async (alle: OnboardingAntwort[]) => {
    setPhase('speichern');
    const probleme: string[] = [];
    let okCount = 0;
    for (let i = 0; i < alle.length; i++) {
      const r = await memcoreOnboardingSpeichern(curatorUrl, ctx, alle[i].frage, alle[i].antwort, i);
      if (r.status === 'ok' || r.status === 'duplikat') {
        okCount += 1;
        setGespeichert((g) => g + 1);
      } else {
        probleme.push(`Frage ${i + 1}: ${curatorFehlertext(r)}`);
      }
    }
    if (okCount > 0) onSaved?.();
    setFehler(probleme);
    setPhase('fertig');
  };

  const onboardingErledigt = phase === 'fertig' && gespeichert > 0;

  return (
    <ModalShell title="ARKO kennenlernen — Onboarding" onClose={() => onClose(onboardingErledigt)} wide>
      {phase === 'fragen' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Frage {index + 1} von {ONBOARDING_FRAGEN.length}
            </span>
            <span className="text-[11px] text-slate-600">{antworten.length} beantwortet</span>
          </div>
          <p className="text-sm font-medium leading-relaxed text-slate-100">{frage}</p>
          <textarea
            value={eingabe}
            onChange={(e) => setEingabe(e.target.value)}
            rows={3}
            placeholder="Antwort eingeben… (leer lassen = überspringen)"
            className="w-full scroll-mt-4 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-blue-500/60 focus:outline-none"
            onFocus={(e) => {
              requestAnimationFrame(() => {
                const el = e.currentTarget;
                if (el && typeof el.scrollIntoView === 'function') {
                  el.scrollIntoView({ block: 'nearest', behavior: 'instant' });
                }
              });
            }}
          />
          <p className="text-[11px] text-slate-600">{PII_HINWEIS}</p>
          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="rounded-lg px-3 py-1.5 text-xs text-slate-500 transition-colors hover:text-slate-300"
            >
              Später
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => weiter(true)}
                className={`rounded-lg border px-3 py-1.5 text-xs ${PORTAL_BTN.secondary}`}
              >
                Überspringen
              </button>
              <button
                type="button"
                onClick={() => weiter(false)}
                disabled={!eingabe.trim() && !letzte}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                  !eingabe.trim() && !letzte ? PORTAL_BTN.secondaryDisabled : PORTAL_BTN.primary
                }`}
              >
                {letzte ? 'Abschließen' : 'Weiter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === 'speichern' && (
        <div className="flex flex-col items-center gap-3 py-6">
          <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          <p className="text-sm text-slate-300">
            ARKO legt Ihre ersten Vermerke an… ({gespeichert}/{antworten.length})
          </p>
          <p className="text-[11px] text-slate-600">Jede Antwort läuft durch PII-Gate und Destillat.</p>
        </div>
      )}

      {phase === 'fertig' && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>
              {gespeichert} von {antworten.length} Antworten im Gedächtnis abgelegt. Sichtbar unter
              „Brain“.
            </span>
          </div>
          {fehler.length > 0 && (
            <div className="space-y-1 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">
              {fehler.map((f) => (
                <p key={f}>{f}</p>
              ))}
            </div>
          )}
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={() => onClose(onboardingErledigt)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${PORTAL_BTN.primary}`}
            >
              Fertig
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
};
